import type { Address, Hex } from 'viem'

import type { TransactionErrorCategory } from '../../../utils'
import type { ActionFlowErrorType, ActionFlowState, DebugBundle, Step } from '../types.action-flow'

/** Canonical sanitized metadata for a caught ActionFlow execution error. */
export interface ActionFlowErrorMetadata {
  /** Stable id shared across this attempt's lifecycle log, analytics event, and occurrence. */
  submissionId: string
  /** Coarse failure classification driving observability tagging. */
  errorType: ActionFlowErrorType
  /** Granular transaction error category (BetterStack `metadata_error_category`). */
  errorCategory?: TransactionErrorCategory
  error_message: string
  flowLabel?: string
  /** Optional tag identifying the action flow. */
  flowTag?: string
  chainId?: number
  flowState: ActionFlowState['flowState']
  stepIndex: number
  stepLabel?: string
  stepType?: Step['type']
  /** Whether the failing step was submitted through atomic wallet batching. */
  isBatch: boolean
  walletAddress?: Address
  txHash?: Hex
  revertErrorName?: string
  debug?: DebugBundle
}

export type BuildActionFlowErrorMetadataParams = Omit<
  ActionFlowErrorMetadata,
  'error_message' | 'stepLabel' | 'stepType' | 'isBatch'
> & {
  error: Error
  step: Step | undefined
  /** Connector + Safe classification from `useActionFlowWalletMetadata`. */
  walletMetadata: Record<string, unknown>
  /** Per-flow analytics context (sessionId, category, order fields, …). */
  trackingContext: Record<string, unknown>
}

/** Assemble canonical error metadata, with explicit fields overriding context. */
export function buildActionFlowErrorMetadata({
  submissionId,
  error,
  errorType,
  errorCategory,
  flowLabel,
  flowTag,
  chainId,
  flowState,
  step,
  stepIndex,
  walletAddress,
  walletMetadata,
  trackingContext,
  txHash,
  revertErrorName,
  debug
}: BuildActionFlowErrorMetadataParams): ActionFlowErrorMetadata {
  return {
    ...walletMetadata,
    ...trackingContext,
    submissionId,
    errorType,
    ...(errorCategory && { errorCategory }),
    error_message: error.message,
    ...(flowLabel !== undefined && { flowLabel }),
    ...(flowTag !== undefined && { flowTag }),
    ...(chainId !== undefined && { chainId }),
    flowState,
    stepIndex,
    ...(step && { stepLabel: step.label, stepType: step.type }),
    isBatch: step?.type === 'batchTx',
    ...(walletAddress && { walletAddress }),
    ...(txHash && { txHash }),
    ...(revertErrorName && { revertErrorName }),
    ...(debug && { debug })
  }
}
